"use client";

import { useCallback, useEffect } from "react";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";

import ShinyText from "@/components/shiny-text";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { isOnboardingV2Incomplete } from "@/data/onboarding-v2";
import { useLoggedUser } from "@/hooks/use-logged-user";
import { isValidOrgId } from "@/lib/utils";
import { needsOrgIdResolution } from "@/routes";

function DashboardRedirect() {
  const { orgId, user, isLoading, isError, refetch } = useLoggedUser();
  const { status: sessionStatus } = useSession();
  const routeParams = useParams();
  const routeOrgId = routeParams?.orgId as string | undefined;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handleRedirect = useCallback(async () => {
    // Check if there's a pending invite in sessionStorage
    // If so, wait a bit for AcceptExistingUserInvite to process it
    if (typeof window !== "undefined" && user?.email) {
      const inviteSignature = `invt_${btoa(user.email)}`;
      const hasPendingInvite = sessionStorage.getItem(inviteSignature);

      if (hasPendingInvite) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        await refetch();
      }
    }

    // If user has orgs but orgId is not set yet (query might be refetching),
    // use the first org from userOrgs. Ignore non-GUID atoms (e.g. "dashboard" from /dashboard route).
    const effectiveOrgId =
      (orgId && isValidOrgId(orgId) ? orgId : undefined) ??
      user?.userOrgs?.[0]?.id;
    const pathnameNoQuery = pathname.split("?")[0];
    const queryString = searchParams.toString();
    const pathnameQuery = queryString ? `?${queryString}` : "";
    const isPseudoOrgPath =
      routeOrgId !== undefined && !isValidOrgId(routeOrgId);

    let redirectTo: string;
    if (!effectiveOrgId) {
      // 401 on /me returns null (not isError) — useLoggedUser navigates to sign-out; do not send user to onboarding.
      if (!isLoading && user === null && !isError) {
        return;
      }
      // Only redirect to onboarding when we successfully loaded user and they have no orgs.
      // Do not redirect on API errors (e.g. 500) — show error state and let user retry.
      redirectTo = isError ? pathname : "/auth/new-onboarding";
    } else if (user?.email) {
      const incompleteOnboarding = await isOnboardingV2Incomplete(user.email);
      if (
        incompleteOnboarding &&
        pathnameNoQuery !== "/auth/new-onboarding"
      ) {
        redirectTo = "/auth/new-onboarding";
      } else if (pathnameNoQuery === "/products") {
        redirectTo = `/${effectiveOrgId}/products${pathnameQuery}`;
      } else if (pathnameNoQuery === "/projects") {
        redirectTo = `/${effectiveOrgId}/projects${pathnameQuery}`;
      } else if (pathnameNoQuery === "/project-templates") {
        redirectTo = `/${effectiveOrgId}/project-templates${pathnameQuery}`;
      } else if (pathnameNoQuery === "/dashboard" || isPseudoOrgPath) {
        redirectTo = `/${effectiveOrgId}/dashboard${pathnameQuery}`;
      } else if (pathnameNoQuery === `/${effectiveOrgId}`) {
        redirectTo = `/${effectiveOrgId}/dashboard${pathnameQuery}`;
      } else {
        redirectTo = pathname;
      }
    } else if (pathnameNoQuery === "/products") {
      // Middleware may redirect to /products?view=...; add orgId
      redirectTo = `/${effectiveOrgId}/products${pathnameQuery}`;
    } else if (pathnameNoQuery === "/projects") {
      redirectTo = `/${effectiveOrgId}/projects${pathnameQuery}`;
    } else if (pathnameNoQuery === "/project-templates") {
      redirectTo = `/${effectiveOrgId}/project-templates${pathnameQuery}`;
    } else if (pathnameNoQuery === "/dashboard" || isPseudoOrgPath) {
      redirectTo = `/${effectiveOrgId}/dashboard${pathnameQuery}`;
    } else if (pathnameNoQuery === `/${effectiveOrgId}`) {
      // Org root (e.g. /my-org-id): default to dashboard
      redirectTo = `/${effectiveOrgId}/dashboard${pathnameQuery}`;
    } else {
      redirectTo = pathname;
    }

    if (pathname !== redirectTo) {
      // Post-login redirect to products/templates: ensure sidebar starts collapsed
      const isPostLoginRedirect =
        pathnameNoQuery === "/dashboard" ||
        pathnameNoQuery === "/products" ||
        pathnameNoQuery === "/projects" ||
        pathnameNoQuery === "/project-templates" ||
        isPseudoOrgPath;
      if (
        isPostLoginRedirect &&
        (redirectTo.includes("/products") ||
          redirectTo.includes("/projects") ||
          redirectTo.includes("/project-templates"))
      ) {
        try {
          localStorage.setItem("dashboardSidebarOpen", "false");
        } catch {
          // ignore (e.g. private mode)
        }
      }
      // Full navigation: soft router.push can reuse stale RSC for /auth/onboarding and org routes; refresh fixes it.
      const isFromReservedRoot =
        pathnameNoQuery === "/dashboard" ||
        pathnameNoQuery === "/products" ||
        pathnameNoQuery === "/projects" ||
        pathnameNoQuery === "/project-templates" ||
        isPseudoOrgPath;
      if (typeof globalThis.window !== "undefined" && isFromReservedRoot) {
        const goToOrgLanding =
          effectiveOrgId &&
          isValidOrgId(effectiveOrgId) &&
          redirectTo.startsWith(`/${effectiveOrgId}/`);
        const goToOnboarding =
          redirectTo === "/auth/onboarding" ||
          redirectTo === "/auth/new-onboarding";
        if (goToOrgLanding || goToOnboarding) {
          globalThis.window.location.assign(redirectTo);
          return;
        }
      }
      router.push(redirectTo);
    }
  }, [
    orgId,
    router,
    user,
    pathname,
    searchParams,
    isError,
    isLoading,
    refetch,
    routeOrgId,
  ]);

  useEffect(() => {
    if (sessionStatus === "loading" || isLoading) {
      return;
    }

    if (needsOrgIdResolution(pathname, routeOrgId)) {
      void handleRedirect();
    }
  }, [
    isLoading,
    handleRedirect,
    pathname,
    routeOrgId,
    sessionStatus,
    user,
  ]);

  if (isError) {
    return (
      <div className="animate-fadeInDown">
        <Card className="w-full max-w-md overflow-hidden rounded-none border-0 bg-background shadow-2xl backdrop-blur-sm sm:w-[448px] sm:rounded-xl">
          <CardHeader>
            <CardTitle className="text-2xl font-semibold tracking-tight">
              Something went wrong
            </CardTitle>
            <CardDescription>
              We couldn&apos;t load your account. This might be a temporary issue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => refetch()} variant="default">
              Try again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-fadeInDown">
      <Card className="w-full max-w-md overflow-hidden rounded-none border-0 bg-background shadow-2xl backdrop-blur-sm sm:w-[448px] sm:rounded-xl">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold tracking-tight">
            Loading...
          </CardTitle>
          {/* <CardDescription>We are preparing your experience</CardDescription> */}
        </CardHeader>
        <CardContent className="relative">
          <ShinyText text="We are preparing your experience, please wait" />
        </CardContent>
      </Card>
    </div>
  );
}
export default DashboardRedirect;
